import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";

// Libellés humains des types de demande
const REQUEST_TYPE_LABELS = {
  early_arrival: "⏩ Arrivée anticipée",
  late_departure: "⚠️ Départ tardif",
  service_change: "✏️ Modification des services",
  modify: "🔧 Modification de séjour",
  cancel: "🚫 Annulation de séjour",
  new: "🆕 Nouvelle demande",
  other: "📨 Autre demande",
};

// Version courte, sans emoji, pour les emails/messages en FR
const REQUEST_TYPE_LABELS_FR_PLAIN = {
  early_arrival: "arrivée anticipée",
  late_departure: "départ tardif",
  service_change: "modification des services",
  modify: "modification de séjour",
  cancel: "annulation de séjour",
  new: "nouvelle demande",
  other: "demande",
};

const REQUEST_TYPE_LABELS_EN_PLAIN = {
  early_arrival: "early arrival",
  late_departure: "late departure",
  service_change: "service change",
  modify: "stay modification",
  cancel: "stay cancellation",
  new: "new request",
  other: "request",
};

// Formate une date ISO en JJ/MM/AAAA (français) ou DD/MM/YYYY (anglais)
function formatDateForLang(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return lang === "en" ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
}

// Libellés humains pour les items de puériculture
const PUERICULTURE_LABELS = {
  lit_bebe: "👶 Lit bébé",
  chaise_haute: "🍽️ Chaise haute",
  baignoire: "🛁 Baignoire bébé",
  barriere: "🚧 Barrière de sécurité",
  reducteur: "🚽 Réducteur WC",
};

const WELCOME_BASKET_LABELS = {
  standard: "🧺 Panier standard",
  premium: "🧺 Panier premium",
};

// Convertit un payload JSON en liste de chips lisibles
function humanizePayload(payload) {
  if (!payload || typeof payload !== "object") return [];
  const items = [];

  if (payload.cleaning === true) items.push("✅ Ménage");
  if (typeof payload.linen_kits_simple === "number" && payload.linen_kits_simple > 0) {
    items.push(`🛏 Kits linge simple : ${payload.linen_kits_simple}`);
  }
  if (typeof payload.linen_kits_double === "number" && payload.linen_kits_double > 0) {
    items.push(`🛏 Kits linge double : ${payload.linen_kits_double}`);
  }
  if (typeof payload.beds_made_simple === "number" && payload.beds_made_simple > 0) {
    items.push(`🛏 Lits faits simple : ${payload.beds_made_simple}`);
  }
  if (typeof payload.beds_made_double === "number" && payload.beds_made_double > 0) {
    items.push(`🛏 Lits faits double : ${payload.beds_made_double}`);
  }
  if (payload.hygiene_kit === true) items.push("🧴 Kit hygiène");
  if (payload.degreasing === true) items.push("🧽 Dégraissage");
  if (payload.welcome_basket && payload.welcome_basket !== "none") {
    items.push(WELCOME_BASKET_LABELS[payload.welcome_basket] || `🧺 Panier d'accueil : ${payload.welcome_basket}`);
  }
  if (Array.isArray(payload.puericulture_items) && payload.puericulture_items.length > 0) {
    for (const it of payload.puericulture_items) {
      items.push(PUERICULTURE_LABELS[it] || `👶 ${it}`);
    }
  }
  if (typeof payload.requested_time === "string" && payload.requested_time) {
    items.push(`🕐 Heure demandée : ${payload.requested_time}`);
  }
  return items;
}

// Extrait la note libre du payload (sans les chips de service)
function extractPayloadNote(payload) {
  if (!payload || typeof payload !== "object") return "";
  const n = payload.notes_planning || payload.note || payload.reason || "";
  return typeof n === "string" ? n.trim() : "";
}

// Convertit un timestamp ISO UTC en chaîne Europe/Paris lisible : "JJ/MM/AAAA à HH:MM"
function formatParisDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const opts = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" };
    const parts = new Intl.DateTimeFormat("fr-FR", opts).formatToParts(d);
    const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return `${m.day}/${m.month}/${m.year} à ${m.hour}:${m.minute}`;
  } catch (err) {
    console.warn("formatParisDateTime failed", err);
    return iso;
  }
}

export default function AdminRequests({ requests, decisionById, setDecisionById, onRefresh }) {
  const [detail, setDetail] = useState(null);
  // Conflits sur les nouvelles dates (uniquement pour type=modify avec start_date/end_date dans payload)
  const [conflicts, setConflicts] = useState(null); // { has_hard_conflict, has_soft_conflict, hard, soft } | null
  const [conflictsLoading, setConflictsLoading] = useState(false);
  // Mini-formulaire "Prévenir le client"
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySubject, setNotifySubject] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  // Charge les conflits quand un détail modify est ouvert
  useEffect(() => {
    if (!detail || detail.request_type !== "modify") { setConflicts(null); return; }
    const p = detail.payload || {};
    if (!p.start_date || !p.end_date) { setConflicts(null); return; }
    let cancelled = false;
    (async () => {
      setConflictsLoading(true);
      try {
        const res = await api.get(`/admin/requests/${detail.id}/conflicts`);
        if (!cancelled) setConflicts(res.data);
      } catch (e) {
        if (!cancelled) setConflicts({ has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [], error: formatError(e) });
      } finally {
        if (!cancelled) setConflictsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detail]);

  // Pré-remplit le message "Prévenir le client" selon la langue owner
  const openNotifyClient = () => {
    if (!detail) return;
    const lang = detail.owner_language === "en" ? "en" : "fr";
    const firstName = detail.owner_first_name || "";
    const propertyName = detail.property_name || "";
    const p = detail.payload || {};
    const newStart = formatDateForLang(p.start_date, lang);
    const newEnd = formatDateForLang(p.end_date, lang);
    if (lang === "en") {
      setNotifySubject("Your date change request cannot be approved — Blanc des Montagnes");
      setNotifyBody(
        `Hello ${firstName},\n\n` +
        `We have carefully reviewed your request to change the dates of your stay at ${propertyName} to ${newStart} – ${newEnd}.\n\n` +
        `Unfortunately, these new dates are not available: another confirmed stay is already booked on the same period.\n\n` +
        `Could you please propose alternative dates? We will do our best to accommodate your new choice.\n\n` +
        `Best regards,\nThe Blanc des Montagnes team`
      );
    } else {
      setNotifySubject("Votre demande de changement de dates ne peut pas être acceptée — Blanc des Montagnes");
      setNotifyBody(
        `Bonjour ${firstName},\n\n` +
        `Nous avons bien reçu votre demande de modification des dates de votre séjour à ${propertyName} pour la période du ${newStart} au ${newEnd}.\n\n` +
        `Malheureusement, ces nouvelles dates ne sont pas disponibles : un autre séjour confirmé est déjà réservé sur cette période.\n\n` +
        `Pourriez-vous nous proposer d'autres dates ? Nous ferons de notre mieux pour vous accommoder.\n\n` +
        `Cordialement,\nL'équipe Blanc des Montagnes`
      );
    }
    setNotifyOpen(true);
  };

  const sendNotifyClient = async () => {
    if (!detail) return;
    setNotifyLoading(true);
    try {
      await api.post(`/admin/requests/${detail.id}/notify-client`, {
        subject: notifySubject,
        body: notifyBody,
      });
      toast({ title: "Email envoyé au propriétaire" });
      setNotifyOpen(false);
    } catch (e) {
      toast({ title: "Erreur d'envoi", description: formatError(e), variant: "destructive" });
    } finally {
      setNotifyLoading(false);
    }
  };

  const decide = async (requestId, decision) => {
    // Récupère le prix par défaut depuis la request (fiche logement)
    const req = requests.find((r) => r.id === requestId);
    const defaultPrice = Number(req?.property_early_late_price_ttc || 25);
    const state = decisionById[requestId] || { price_ttc: defaultPrice, is_free: false, admin_decision_note: "" };
    // Pour un refus, la note est fortement recommandée — confirmer si vide
    if (decision === "reject" && !(state.admin_decision_note || "").trim()) {
      if (!window.confirm("Refuser cette demande sans motif ? Le propriétaire recevra un email sans explication. Cliquez Annuler pour saisir une note.")) {
        return;
      }
    }
    // ─── Règle métier : seuls "early_arrival" et "late_departure" sont payants.
    // Modification / Annulation sont toujours gratuits — on force price_ttc=0 et
    // is_free=true côté frontend pour éviter que le prix par défaut (25€) ne
    // se propage dans l'audit log ni dans le champ price_ttc de la request.
    // Le backend applique la même garantie (defense in depth).
    const isPricedRequestType = req?.request_type === "early_arrival" || req?.request_type === "late_departure";
    const priceTtc = isPricedRequestType ? (state.is_free ? 0 : Number(state.price_ttc || 0)) : 0;
    const isFree = isPricedRequestType ? !!state.is_free : true;
    try {
      await api.post(`/admin/requests/${requestId}/decide`, {
        decision,
        admin_decision_note: state.admin_decision_note || "",
        price_ttc: priceTtc,
        is_free: isFree,
      });
      toast({ title: decision === "approve" ? "Demande acceptée" : "Demande refusée" });
      await onRefresh();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.08]">
      <Table data-testid="admin-requests-table">
        <TableHeader>
          <TableRow>
            <TableHead data-testid="admin-requests-col-property">Appartement</TableHead>
            <TableHead data-testid="admin-requests-col-dates">Dates séjour</TableHead>
            <TableHead data-testid="admin-requests-col-type">Type</TableHead>
            <TableHead data-testid="admin-requests-col-payload">Détail</TableHead>
            <TableHead data-testid="admin-requests-col-decision">Décision</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((r) => {
            // Prix par défaut = tarif configuré sur la fiche logement (fallback 25€ si absent)
            const defaultPrice = Number(r.property_early_late_price_ttc || 25);
            const state = decisionById[r.id] || { price_ttc: defaultPrice, is_free: false, admin_decision_note: "" };
            const showPrice = r.request_type === "early_arrival" || r.request_type === "late_departure";
            const payloadChips = humanizePayload(r.payload || {});
            return (
              <TableRow data-testid={`admin-request-row-${r.id}`} key={r.id} className="align-top">
                <TableCell data-testid={`admin-request-property-${r.id}`} className="font-medium align-top cursor-pointer hover:bg-white/[0.04]" onClick={() => setDetail(r)}>
                  {/* Horodatages prominents */}
                  <div className="text-xs text-slate-400 mb-1" data-testid={`admin-request-created-${r.id}`}>
                    📅 Demande reçue le {formatParisDateTime(r.created_at)}
                  </div>
                  {r.decided_at && (
                    <div className="text-xs text-emerald-300 mb-1" data-testid={`admin-request-decided-${r.id}`}>
                      ✅ Décidée le {formatParisDateTime(r.decided_at)}
                    </div>
                  )}
                  <div className="text-slate-100">{r.property_name}</div>
                  {r.owner_display && <div className="text-xs text-slate-400 mt-0.5">👤 {r.owner_display}</div>}
                </TableCell>
                <TableCell data-testid={`admin-request-dates-${r.id}`} className="text-sm cursor-pointer" onClick={() => setDetail(r)}>
                  {formatDateEU(r.stay_start_date)} → {formatDateEU(r.stay_end_date)}
                </TableCell>
                <TableCell data-testid={`admin-request-type-${r.id}`} className="cursor-pointer" onClick={() => setDetail(r)}>
                  {REQUEST_TYPE_LABELS[r.request_type] || r.request_type}
                </TableCell>
                <TableCell data-testid={`admin-request-payload-${r.id}`} className="text-slate-200 cursor-pointer" onClick={() => setDetail(r)}>
                  {payloadChips.length === 0 ? (
                    <span className="italic text-slate-500">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {payloadChips.map((chip, i) => (
                        <span key={i} className="inline-flex items-center rounded-full bg-white/[0.06] border border-white/10 px-2 py-0.5 text-xs text-slate-100">{chip}</span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {(!r.status || r.status === "pending") ? (
                    (r.request_type === "modify" && (r.payload?.start_date || r.payload?.end_date)) ? (
                      // Type "modify" avec changement de dates → passer obligatoirement par le dialog (garde-fou conflits)
                      <div className="space-y-2">
                        <Button
                          data-testid={`admin-request-open-detail-${r.id}`}
                          variant="outline"
                          className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10"
                          onClick={() => setDetail(r)}
                        >
                          🔎 Ouvrir le détail
                        </Button>
                        <div className="text-xs text-slate-400">
                          Changement de dates : décision via le dialog.
                        </div>
                      </div>
                    ) : (
                    <div className="space-y-2">
                      {showPrice && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label data-testid={`admin-request-price-label-${r.id}`} className="text-xs text-slate-200">Prix TTC</Label>
                            <Input data-testid={`admin-request-price-input-${r.id}`} type="number" min={0} className="border-white/10 bg-white/[0.08]" value={state.price_ttc} onChange={(e) => setDecisionById((s) => ({ ...s, [r.id]: { ...state, price_ttc: e.target.value } }))} disabled={state.is_free} />
                          </div>
                          <div className="space-y-1">
                            <Label data-testid={`admin-request-free-label-${r.id}`} className="text-xs text-slate-200">Offrir gracieusement</Label>
                            <label htmlFor={`admin-request-free-checkbox-${r.id}`} className="mt-2 flex cursor-pointer items-center justify-between rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 hover:bg-white/[0.10]">
                              <span className="text-sm text-slate-200">Gratuit {state.is_free ? "✓" : ""}</span>
                              <Checkbox id={`admin-request-free-checkbox-${r.id}`} data-testid={`admin-request-free-checkbox-${r.id}`} checked={!!state.is_free} onCheckedChange={(v) => setDecisionById((s) => ({ ...s, [r.id]: { ...state, is_free: !!v } }))} />
                            </label>
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label data-testid={`admin-request-note-label-${r.id}`} className="text-xs text-slate-200">Note de refus / message au propriétaire</Label>
                        <Textarea data-testid={`admin-request-note-textarea-${r.id}`} placeholder="Motif du refus ou message à inclure dans l'email" className="border-white/10 bg-white/[0.08]" value={state.admin_decision_note} onChange={(e) => setDecisionById((s) => ({ ...s, [r.id]: { ...state, admin_decision_note: e.target.value } }))} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button data-testid={`admin-request-approve-${r.id}`} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" onClick={() => decide(r.id, "approve")}>✅ Accepter</Button>
                        <Button data-testid={`admin-request-reject-${r.id}`} className="rounded-full bg-rose-500 text-white hover:bg-rose-400" onClick={() => decide(r.id, "reject")}>❌ Refuser</Button>
                      </div>
                    </div>
                    )
                  ) : (
                    <div className="space-y-1 text-sm">
                      <div className={r.status === "approved" ? "text-emerald-300" : "text-rose-300"}>
                        {r.status === "approved" ? "✅ Acceptée" : "❌ Refusée"}
                      </div>
                      {r.admin_decision_note && (
                        <div className="text-xs text-slate-300 italic">« {r.admin_decision_note} »</div>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {!requests.length && (
            <TableRow>
              <TableCell data-testid="admin-requests-empty" colSpan={5} className="py-8 text-center text-slate-300">
                Aucune demande en attente.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) { setDetail(null); setConflicts(null); } }}>
        <DialogContent data-testid="admin-request-detail-dialog" className="border-white/10 bg-[#0a1428] text-slate-50 sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-request-detail-title">
              {detail ? (REQUEST_TYPE_LABELS[detail.request_type] || "Détail de la demande") : "Détail de la demande"}
            </DialogTitle>
          </DialogHeader>
          {detail && (() => {
            const p = detail.payload || {};
            const hasDateChange = !!(p.start_date || p.end_date);
            const chips = humanizePayload(p);
            const noteText = extractPayloadNote(p);
            const isPending = !detail.status || detail.status === "pending";
            const isModifyWithDates = detail.request_type === "modify" && hasDateChange;
            const hardConflict = conflicts?.has_hard_conflict === true;
            const softConflict = conflicts?.has_soft_conflict === true;
            const state = decisionById[detail.id] || { price_ttc: Number(detail.property_early_late_price_ttc || 25), is_free: false, admin_decision_note: "" };
            const acceptBlocked = isModifyWithDates && hardConflict;
            return (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-400" data-testid="admin-request-detail-created">📅 Demande reçue le <strong className="text-slate-100">{formatParisDateTime(detail.created_at)}</strong></div>
              {detail.decided_at && (
                <div className="text-xs text-emerald-300" data-testid="admin-request-detail-decided">✅ Décidée le <strong>{formatParisDateTime(detail.decided_at)}</strong></div>
              )}
              <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <dt className="text-slate-400">Logement</dt><dd className="text-slate-100 font-medium" data-testid="admin-request-detail-property">{detail.property_name}</dd>
                {detail.owner_display && <><dt className="text-slate-400">Propriétaire</dt><dd className="text-slate-100" data-testid="admin-request-detail-owner">{detail.owner_display}{detail.owner_language === "en" ? " · EN" : " · FR"}</dd></>}
                <dt className="text-slate-400">Type</dt><dd className="text-slate-100" data-testid="admin-request-detail-type">{REQUEST_TYPE_LABELS[detail.request_type] || detail.request_type}</dd>
              </dl>

              {/* Bloc Dates : distinguer dates ACTUELLES vs DEMANDÉES pour type=modify */}
              {isModifyWithDates ? (
                <div className="rounded-md border border-white/10 bg-white/[0.04] p-3 space-y-1" data-testid="admin-request-detail-dates-block">
                  <div className="text-xs text-slate-400">Dates actuelles</div>
                  <div className="text-slate-100" data-testid="admin-request-detail-current-dates">
                    {formatDateEU(detail.stay_start_date)} → {formatDateEU(detail.stay_end_date)}
                  </div>
                  <div className="text-xs text-slate-400 mt-2">Dates demandées</div>
                  <div className="text-amber-200 font-medium" data-testid="admin-request-detail-requested-dates">
                    {formatDateEU(p.start_date || "")} → {formatDateEU(p.end_date || "")}
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-white/10 bg-white/[0.04] p-3">
                  <div className="text-xs text-slate-400">Dates du séjour</div>
                  <div className="text-slate-100">{formatDateEU(detail.stay_start_date)} → {formatDateEU(detail.stay_end_date)}</div>
                </div>
              )}

              {/* Services demandés — lisibles, jamais de JSON brut */}
              <div>
                <div className="text-xs text-slate-400 mb-1">Services demandés</div>
                {chips.length === 0 ? (
                  <div className="italic text-slate-400" data-testid="admin-request-detail-no-service">Aucun service demandé.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="admin-request-detail-services">
                    {chips.map((chip, i) => (
                      <span key={i} className="inline-flex items-center rounded-full bg-white/[0.06] border border-white/10 px-2 py-0.5 text-xs text-slate-100">{chip}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Note propriétaire — uniquement si non vide */}
              {noteText && (
                <div>
                  <div className="text-xs text-slate-400 mb-1">Note du propriétaire</div>
                  <div className="rounded-md bg-white/[0.05] p-2 text-slate-200" data-testid="admin-request-detail-owner-note">{noteText}</div>
                </div>
              )}

              {/* Bandeau de conflit pour modify+dates */}
              {isModifyWithDates && conflictsLoading && (
                <div className="rounded-md border border-white/10 bg-white/[0.05] p-2 text-xs text-slate-300" data-testid="admin-request-detail-conflicts-loading">
                  Vérification des disponibilités en cours…
                </div>
              )}
              {isModifyWithDates && !conflictsLoading && hardConflict && (
                <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 space-y-1" data-testid="admin-request-detail-conflict-hard">
                  <div className="font-medium text-rose-200">⛔ Ces dates ne sont pas disponibles</div>
                  <div className="text-xs text-rose-100">Un séjour confirmé est déjà réservé sur la période demandée :</div>
                  <ul className="text-xs text-rose-100 list-disc list-inside">
                    {(conflicts.hard || []).map((c) => (
                      <li key={c.stay_id}>{formatDateEU(c.start_date)} → {formatDateEU(c.end_date)}{c.tenant_name ? ` — ${c.tenant_name}` : ""}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-rose-100 pt-1">Le bouton <strong>Accepter</strong> est désactivé.</div>
                </div>
              )}
              {isModifyWithDates && !conflictsLoading && !hardConflict && softConflict && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="admin-request-detail-conflict-soft">
                  <div className="font-medium text-amber-200">⚠️ Attention : chevauchement iCal externe</div>
                  <div className="text-xs text-amber-100">Un événement importé depuis une plateforme externe couvre déjà cette période :</div>
                  <ul className="text-xs text-amber-100 list-disc list-inside">
                    {(conflicts.soft || []).map((c) => (
                      <li key={c.stay_id}>{formatDateEU(c.start_date)} → {formatDateEU(c.end_date)}{c.ical_source ? ` — ${c.ical_source}` : ""}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-100 pt-1">Vous pouvez tout de même accepter en connaissance de cause.</div>
                </div>
              )}

              {/* Bloc décision — visible uniquement si demande en attente */}
              {isPending && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-200">Note admin (optionnelle, incluse dans l'email)</Label>
                    <Textarea
                      data-testid="admin-request-detail-note"
                      placeholder="Message au propriétaire (motif de refus, précisions…)"
                      className="border-white/10 bg-white/[0.08]"
                      value={state.admin_decision_note}
                      onChange={(e) => setDecisionById((s) => ({ ...s, [detail.id]: { ...state, admin_decision_note: e.target.value } }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="admin-request-detail-approve"
                      disabled={acceptBlocked}
                      className={`rounded-full text-white ${acceptBlocked ? "bg-emerald-500/30 cursor-not-allowed" : "bg-emerald-500 hover:bg-emerald-400"}`}
                      onClick={async () => { if (acceptBlocked) return; await decide(detail.id, "approve"); setDetail(null); }}
                    >
                      ✅ Accepter
                    </Button>
                    <Button
                      data-testid="admin-request-detail-reject"
                      className="rounded-full bg-rose-500 text-white hover:bg-rose-400"
                      onClick={async () => { await decide(detail.id, "reject"); setDetail(null); }}
                    >
                      ❌ Refuser
                    </Button>
                    {acceptBlocked && (
                      <Button
                        data-testid="admin-request-detail-notify-client"
                        variant="outline"
                        className="rounded-full border-rose-400/40 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                        onClick={openNotifyClient}
                      >
                        📧 Prévenir le client
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Note admin existante (demande déjà décidée) */}
              {!isPending && detail.admin_decision_note && (
                <div>
                  <div className="text-xs text-slate-400 mb-1">Note admin</div>
                  <div className="rounded-md bg-white/[0.05] p-2 text-slate-200">{detail.admin_decision_note}</div>
                </div>
              )}
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={() => setDetail(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mini-dialog "Prévenir le client" — pré-rempli FR ou EN selon owner.language */}
      <Dialog open={notifyOpen} onOpenChange={(open) => { if (!open) setNotifyOpen(false); }}>
        <DialogContent data-testid="admin-request-notify-dialog" className="border-white/10 bg-[#0a1428] text-slate-50 sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Prévenir le propriétaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-400">
              Un email sera envoyé à {detail?.owner_email || "—"} (langue : {detail?.owner_language === "en" ? "English" : "Français"}).
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-200">Objet</Label>
              <Input
                data-testid="admin-request-notify-subject"
                className="border-white/10 bg-white/[0.08]"
                value={notifySubject}
                onChange={(e) => setNotifySubject(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-200">Message</Label>
              <Textarea
                data-testid="admin-request-notify-body"
                className="border-white/10 bg-white/[0.08] min-h-[240px] font-mono text-xs"
                value={notifyBody}
                onChange={(e) => setNotifyBody(e.target.value)}
              />
              <div className="text-xs text-slate-400">Vous pouvez éditer le message avant l'envoi.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={() => setNotifyOpen(false)}>Annuler</Button>
            <Button
              data-testid="admin-request-notify-send"
              disabled={notifyLoading || !notifySubject.trim() || !notifyBody.trim()}
              className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-50"
              onClick={sendNotifyClient}
            >
              {notifyLoading ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
